import React from 'react';
import { experience } from '../data/mock';
import { useInView } from '../hooks/useInView';
import { Badge } from './ui/badge';

const ExperienceCard = ({ exp, index }) => {
  const [ref, isInView] = useInView({ threshold: 0.15 });

  return (
    <div
      ref={ref}
      className={`relative group transition-all duration-700 ease-out ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      {/* Timeline dot */}
      <div className="absolute left-0 top-10 z-10 -translate-x-[5.5px]">
        <div className={`w-[11px] h-[11px] rounded-full border-2 ${
          exp.current ? 'border-[#0a0a0a] bg-[#0a0a0a]' : 'border-gray-300 bg-white'
        }`}>
          {exp.current && (
            <span className="absolute inset-[-4px] rounded-full border border-[#0a0a0a]/20 animate-ping" />
          )}
        </div>
      </div>

      {/* Card */}
      <div
        className={`ml-10 p-7 md:p-8 rounded-2xl bg-white border transition-all duration-400 relative overflow-hidden ${
          exp.current
            ? 'border-gray-200 shadow-[0_2px_20px_rgba(0,0,0,0.04)] border-l-[3px] border-l-[#0a0a0a]'
            : 'border-gray-100 hover:border-gray-200 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)]'
        }`}
      >
        {/* Watermark */}
        <span className="absolute -right-2 -top-3 text-[5.5rem] md:text-[7rem] font-heading font-[800] text-gray-50/80 leading-none select-none pointer-events-none tracking-tight">
          {exp.company.split(' ')[0]}
        </span>

        <div className="relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-1">
            <div>
              <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-tight">
                {exp.company}
              </h3>
              <p className="text-gray-500 text-[15px] mt-1">{exp.role}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {exp.current && (
                <Badge variant="outline" className="border-[#0a0a0a] text-[#0a0a0a] text-[11px] font-mono rounded-full px-3 h-6">
                  Current
                </Badge>
              )}
              <span className="text-[11px] font-mono text-gray-300 hidden sm:inline">
                {exp.type}
              </span>
            </div>
          </div>

          <p className="font-mono text-[11px] text-gray-300 mb-4 tracking-wide">{exp.duration}</p>
          <p className="text-gray-400 leading-relaxed text-[15px] mb-5">{exp.description}</p>

          <div className="flex flex-wrap gap-2">
            {exp.tech.map((t) => (
              <span
                key={t}
                className="text-[11px] font-mono px-3 py-1 bg-[#fafafa] text-gray-500 rounded-full border border-gray-100/60"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ExperienceSection = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.3 });

  return (
    <section id="experience" className="py-24 md:py-32 bg-[#FAFAFA]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div
          ref={headerRef}
          className={`flex items-end gap-8 mb-16 transition-all duration-700 ${
            headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <p className="font-mono text-[12px] text-gray-400 tracking-widest uppercase mb-4">
              Career Journey
            </p>
            <h2 className="font-heading text-4xl md:text-[3.25rem] font-[200] tracking-tight leading-tight">
              Experience<span className="text-gray-200">.</span>
            </h2>
          </div>
          <div className="hidden md:flex items-baseline gap-2 pb-1">
            <span className="font-heading text-5xl font-[200] text-gray-200">2+</span>
            <span className="text-[11px] text-gray-300 font-mono tracking-wide">years</span>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-gray-200" />

          <div className="space-y-6">
            {experience.map((exp, index) => (
              <ExperienceCard key={exp.id} exp={exp} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
